import get from "lodash/get";
import toLower from "lodash/toLower";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import BuildProgramStepOneModal from "../../../parts/modals/add-cycle";
import EditProgramSettingsModal from "../../../parts/modals/edit-program-settings";
import LearnMoreModal from "../../../parts/modals/learn-more";
import ManageCategoriesModal from "../../../parts/modals/manage-categories";
import SortCategoriesModal from "../../../parts/modals/sort-categories";
import { fetchSendProjection } from "../../../utils/projection-helpers";
import { loadCopiableCycles } from "../../../utils/copiable-cycle";
import { getPushTestEmail } from "../../../utils/user-helpers";
import VideoCenterWatch from "../../athlete-portal/profile/components/VideoCenterWatch";
import AssignSingleWorkoutClients from "../all-templates/components/AssignSingleWorkoutClients";
import FetchModal from "../common-components/FetchModal";
import SendModal from "../common-components/SendModal";
import PublishTeam from "../teams/components/PublishTeam";
import TeamPublished from "../teams/components/TeamPublished";
import TeamUnpublish from "../teams/components/TeamUnpublish";
import TestPushAccountModal from "./components/TestPushAccountModal";
import Store from "./Store";

const StoreController = async ({ res, req, locals, load, api }) => {
  const { query = {} } = req;
  const coachOrganizationPermissions =
    locals?.activeUser?.organizationPermissions || [];
  // First look for the permissions that allow the coach to acces to the store -
  // - in the ORGANIZATION permissions
  // These are by now "Templates" and "PUSH"
  const organizationStorePermissions =
    locals?.organizationPermissions?.filter((permission) =>
      ["PUSH"].includes(permission.permission)
    ) || {};

  // Then, in the permisisons of the COACH, find the a permissions that matches with the ones that
  // allow the coach to access to the store
  if (
    locals?.activeUser?.organizationUserStatus &&
    !organizationStorePermissions.some((storePermission) =>
      coachOrganizationPermissions.includes(storePermission._id)
    )
  )
    return res.redirect("/coaching-portal/athletes");
  const proxyAthlete = query.athlete;
  if (query.menuExpanded) res.refreshWith({ menuExpanded: false });
  const loadPromises = [load("storePrograms")];
  const modal = get(query, "modal", {});

  const queryEditCycleSettings = {
    cycle: query.cycleId,
    proxyAthlete,
  };

  //The user is allow to see al the athletes of the coaches
  const isAllowToViewAllOrganizationsAthletes =
    locals.organizationRoleName &&
    (locals.organizationRoleName === "Owner" ||
      locals.organizationRoleName === "Admin");

  switch (modal.type) {
    case "edit-cycle-settings":
    case "edit-program-settings":
      loadPromises.push(load("editCycle", queryEditCycleSettings));
      break;
    case "learn-more":
      loadPromises.push(
        load("editCycle", { cycle: query.cycleId }),
        load("programExercises", { programId: query.cycleId })
      );
      break;
    case "send":
      loadPromises.push(
        load("athleteCycles", { customProjection: fetchSendProjection }),
        load("allAthletesFromOrganization"),
        load("clientsOnProgram")
      );
      if (query.recipient)
        loadPromises.push(load("unPushedAthleteCyclesNames", query.recipient));
      break;
    case "fetch":
      loadPromises.push(
        load("allAthletesFromOrganization"),
        loadCopiableCycles(
          { api, locals },
          query.fetchSourceOwner || locals.activeUserToRequest._id
        )
      );
      break;
    case "assign-single-workout-clients":
      loadPromises.push(load("allAthletesFromOrganization"));
      if (query.cycleId)
        loadPromises.push(load("editCycle", { cycle: query.cycleId }));
      break;
    default:
      break;
  }

  if (locals.activeUserToRequest?.email) {
    const testEmail = getPushTestEmail(locals.activeUserToRequest.email);
    loadPromises.push(load("userByEmail", { email: toLower(testEmail) }));
  }

  await Promise.all(loadPromises);

  const { storePrograms, activeUserToRequest } = locals;

  const {
    pushAccountInfo: {
      mostPopular = null,
      programCategories = [],
      mostPopularSingle,
    } = {},
  } = activeUserToRequest;

  const refresh = (selectedCategoryIndex = "", category = "") => {
    res.refreshWith(
      {
        selectedCategoryIndex,
        category,
        newCategory: "",
        deleteIndex: "",
        update: "",
        favorite: "",
        status: query.status || "published",
      },
      { url: "/coaching-portal/store", hash: "#_" }
    );
  };

  const isNewCategory = Boolean(query.newCategory && !query.modal);
  const isDeletingCategory = Boolean(
    query.deleteIndex && typeof query.deleteIndex === "number"
  );
  const isSelectAll = Boolean(
    query.selectedCategoryIndex && query.selectedCategoryIndex === "all"
  );
  const isFavorite = Boolean(query.favorite);
  const isAnUpdate = Boolean(query.update);

  if (isAnUpdate) refresh(query.selectedCategoryIndex, query.category);

  if (isNewCategory || isDeletingCategory || isSelectAll || isFavorite)
    refresh();

  locals.modal = {
    "build-step-one": <BuildProgramStepOneModal />,
    "edit-cycle-settings": <BuildProgramStepOneModal />,
    "edit-program-settings": <EditProgramSettingsModal />,
    "learn-more": <LearnMoreModal />,
    "manage-categories": (
      <ManageCategoriesModal
        category={query.category}
        selectedCategoryIndex={query.selectedCategoryIndex}
        programs={storePrograms[query.status || "published"]}
        //restOfPrograms is to edit a category in both published and draft programs
        restOfPrograms={
          storePrograms[query.status === "draft" ? "published" : "draft"]
        }
        newCategory={query.newCategory || false}
        onSave={() => {}}
        storeType={query.storeType || "programs"}
        programCategories={programCategories || []}
      />
    ),
    "sort-categories": (
      <SortCategoriesModal
        activeUserToRequest={locals.activeUserToRequest}
        categories={programCategories || []}
        onSave={() => {}}
      />
    ),
    fetch: (
      <FetchModal
        subscription
        isAllowToViewAllOrganizationsAthletes={
          isAllowToViewAllOrganizationsAthletes
        }
      />
    ),
    send: <SendModal />,
    "test-account": <TestPushAccountModal />,
    "unpublish-team": (
      <TeamUnpublish cycleId={query.cycleId} cycleName={query.cycleName} />
    ),
    "publish-team": (
      <PublishTeam
        locals={locals}
        cycleId={query.cycleId}
        cycleName={query.cycleName}
      />
    ),
    "team-published": <TeamPublished locals={locals} />,
    "activity-feed": <ActivityDrawer />,
    "watch-loom-video": (
      <VideoCenterWatch
        sharedUrl={query.sharedUrl}
        locals={locals}
        title={"Team's Intro"}
      />
    ),
    "assign-single-workout-clients": (
      <AssignSingleWorkoutClients locals={locals} />
    ),
  }[modal.type];

  query.sort = query.sort || "modifiedDate";

  res.body = (
    <Store
      total={20}
      query={query}
      mostPopular={mostPopular}
      programCategories={programCategories}
      mostPopularSingle={mostPopularSingle}
    />
  );
};

export default StoreController;
